import { Provider } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { HistoryRouter as Router } from 'redux-first-history/rr6';
import { ReactNode, useEffect } from 'react';
import { PersistGate } from 'redux-persist/integration/react';
import { v4 as uuidv4 } from 'uuid';

import { store, history, persistor } from './redux/configure-store';
import { CustomNoticeBar } from './components/custom-notice-bar';
import { useScrollToTop } from './hooks/use-scroll-to-top';
import { routes } from './routes/routes';
import { getNativeDeviceInfo, fingerprintTrustSdk } from './hooks/use-get-native-info';
import './app.css';
import { usePinTimers } from './hooks/use-pin-timers';
import { getUtmCampaign } from './hooks/use-utm-campaign';
import { useFingerprint } from './hooks/use-fingerprint';
import { useAppSelector } from './hooks/use-redux-hook';
import { deviceNativeInfoSelector } from './redux/modules/app';
import { isWebContext } from './utils/is-web-context';
import { LOCAL_STORAGE } from './constants/local-storage';
import { showAnalytics } from './utils/show-on-stand';

const ScrollToTopWrapper = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const { pathname } = useLocation();

  useScrollToTop(pathname);
  usePinTimers();
  getUtmCampaign();
  useFingerprint();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const paramsToCheck = [
      'utm_campaign',
      'utm_medium',
      'utm_term',
      'utm_content',
      'gclid',
      'fbclid',
      'ttclid',
    ];

    const hasParam = paramsToCheck.some((param) => urlParams.has(param));

    if (hasParam) {
      sessionStorage.setItem(LOCAL_STORAGE.utmPresent, 'true');
    } else {
      sessionStorage.setItem(LOCAL_STORAGE.utmPresent, 'false');
    }
  }, []);

  useEffect(() => {
    if (isWebContext && !localStorage.getItem(LOCAL_STORAGE.platform)) {
      localStorage.setItem(LOCAL_STORAGE.platform, 'WEB');
    }
    if (isWebContext && !localStorage.getItem(LOCAL_STORAGE.deviceId)) {
      localStorage.setItem(LOCAL_STORAGE.deviceId, uuidv4());
    }
    if (isWebContext) {
      navigator.permissions.query({ name: 'notifications' }).then((result) => {
        if (result.state === 'prompt') {
          localStorage.removeItem(LOCAL_STORAGE.savedPushToken);
        }
      });
    }
    if (showAnalytics()) {
      // eslint-disable-next-line
      fingerprintTrustSdk().catch((e) => console.error(e));
    }
  }, []);

  return children;
};

const NativeWrapper = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const { appVersion } = useAppSelector(deviceNativeInfoSelector);

  useEffect(() => {
    if (window) {
      getNativeDeviceInfo();
    }
  }, []);

  if (!isWebContext && appVersion) {
    return children;
  }
  if (isWebContext) {
    return children;
  }

  return null;
};

export const App = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <Router history={history}>
        <NativeWrapper>
          <ScrollToTopWrapper>
            {routes}
            <CustomNoticeBar />
          </ScrollToTopWrapper>
        </NativeWrapper>
      </Router>
    </PersistGate>
  </Provider>
);
